import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Award, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AdSlot } from "@/components/AdSlot";
import { ReelCard } from "@/components/OnThisDayReel";
import { HallOfFameCard, ClippingCard, NewsAttribution } from "@/components/HallOfFame";

const MONTHS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
const cap = (s) => s.replace("-", " ").replace(/^\w/, (c) => c.toUpperCase());

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

export default function OddlyTrueNews() {
  const { slug } = useParams();
  const now = new Date();
  const key = (slug || `${MONTHS[now.getUTCMonth()]}-${now.getUTCDate()}`).toLowerCase();
  const [news, setNews] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setNews(null); setError("");
    api.get(`/oddlytrue-news/history/${key}`).then((r) => setNews(r.data)).catch((e) => setError(e.response?.status === 404 ? "That day doesn't exist." : "The Hall of Fame is unavailable right now."));
  }, [key]);

  useEffect(() => {
    if (!news) return;
    const prev = document.title;
    document.title = news.seo.title;
    setMeta("description", news.seo.description);
    return () => { document.title = prev; };
  }, [news]);

  return (
    <div className="min-h-screen bg-paper-light">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="oddlytrue-news-page">
        <div className="border-y-4 border-double border-ink py-4 text-center">
          <div className="flex items-center justify-center gap-2">
            <Award className="w-4 h-4 text-gold" />
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold">Strange but true headlines of the day · Vintage clipping</p>
          </div>
          <h1 data-testid="oddlytrue-news-title" className="mt-2 font-vintage text-4xl sm:text-5xl font-bold text-ink leading-tight tracking-tight">{news ? news.seo.h1 : `OddlyTrue News Stand — ${cap(key)}`}</h1>
          {news && <p className="mt-3 text-base text-ink-muted">One real strange-but-true headline, three more happenings from the date and a page from a {news.vintageClipping?.year || "vintage"} newspaper. <Link to={`/history/${news.slug}`} data-testid="oddlytrue-news-history-link" className="font-semibold text-gold hover:underline">See the full Vintage News edition →</Link></p>}
        </div>

        {news && (
          <div className="mt-5 flex items-center justify-between gap-2">
            <Link to={`/oddlytrue-news/${news.prev_slug}`} data-testid="oddlytrue-news-prev" className="inline-flex items-center gap-1 px-4 h-10 border border-ink bg-[#fbf8f1] text-sm font-semibold text-ink hover:bg-ink hover:text-white transition-colors"><ChevronLeft className="w-4 h-4" /> {cap(news.prev_slug)}</Link>
            <Link to={`/oddlytrue-news/${news.next_slug}`} data-testid="oddlytrue-news-next" className="inline-flex items-center gap-1 px-4 h-10 border border-ink bg-[#fbf8f1] text-sm font-semibold text-ink hover:bg-ink hover:text-white transition-colors">{cap(news.next_slug)} <ChevronRight className="w-4 h-4" /></Link>
          </div>
        )}

        {error && <p data-testid="oddlytrue-news-error" className="mt-8 text-sm text-ink-muted">{error} <Link to="/" className="font-semibold text-gold hover:underline">Back to today →</Link></p>}

        {news && (
          <>
            <section className="mt-8">
              {news.stale && <p data-testid="oddlytrue-news-stale" className="mb-3 px-3 py-2 bg-gold/15 border border-gold text-[12px] text-ink">Showing yesterday's oddities while we fetch today's.</p>}
              <HallOfFameCard h={news.hallOfFame} testid="hof-card" slug={news.slug} />
              <p className="mt-2 text-[11px] text-ink-muted">Headline {((news.rotation?.dayOfYear || 0) % (news.rotation?.count || 1)) + 1} of {news.rotation?.count} in the Hall of Fame rotation.</p>
            </section>

            {news.weirdHistory?.length > 0 && (
              <section className="mt-8">
                <h2 className="font-vintage text-xl font-bold text-ink mb-3 border-b-2 border-ink pb-1">Also on {news.date}</h2>
                <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="oddlytrue-news-weird">{news.weirdHistory.map((it, i) => <ReelCard key={`${it.year}-${i}`} it={it} testid={`oddlytrue-news-weird-${i + 1}`} />)}</ul>
              </section>
            )}

            <AdSlot id="adsense-oddlytrue-news" slot="MID" size="rect300" className="mt-8" />

            {news.vintageClipping && (
              <section className="mt-8">
                <h2 className="font-vintage text-xl font-bold text-ink mb-3 border-b-2 border-ink pb-1">Vintage clipping</h2>
                <ClippingCard c={news.vintageClipping} />
              </section>
            )}

            <NewsAttribution news={news} />
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
